import { YStack, Text, styled } from 'tamagui'
import { copy } from '../../content/copy'

const Section = styled(YStack, {
  name: 'LogoBarSection',
  paddingVertical: '$8',
  backgroundColor: '$background',
  alignItems: 'center',
  borderTopWidth: 1,
  borderTopColor: '$border',
  overflow: 'hidden',
  position: 'relative',
})

const Label = styled(Text, {
  name: 'LogoBarLabel',
  fontFamily: '$accent',
  fontSize: '$1',
  color: '$textMuted',
  letterSpacing: 2,
  textTransform: 'uppercase',
  marginBottom: '$5',
})

const LOGO_MAP: Record<string, string> = {
  '3PunchConvo': '3punchconvo',
  AdventHealth: 'adventhealth',
  Amazon: 'amazon',
  Cactus: 'cactus',
  CarMax: 'carmax',
  DigitalOcean: 'digitalocean',
  Django: 'django',
  DOC: 'doc',
  Gatsby: 'gatsby',
  Hasura: 'hasura',
  Kaiber: 'kaiber',
  Lastmile: 'lastmile',
  LeapGrow: 'leapgrow',
  Lightmatter: 'lightmatter',
  Litera: 'litera',
  Microsoft: 'microsoft',
  Nintendo: 'nintendo',
  Observer: 'observer',
  RealGraph: 'realgraph',
  RenewaFi: 'renewafi',
  'STAR/CHILD': 'starchild',
  SurePeople: 'surepeople',
  Tamagui: 'tamagui',
  'True Homes': 'truehomes',
  Uniswap: 'uniswap',
  USDA: 'usda',
  USFS: 'usfs',
  Vercel: 'vercel',
  Wellplaece: 'wellplaece',
  Worldly: 'worldly',
  WPGraphQL: 'wpgraphql',
  XWP: 'xwp',
}

const LOGO_HEIGHT = 22
const SCROLL_DURATION = 80

export function LogoBarSection() {
  const { label, companies } = copy.logoBar
  const logos = companies.map((c) => ({ name: c, slug: LOGO_MAP[c] })).filter((l) => l.slug)

  return (
    <Section>
      <Label>{label}</Label>

      <div className="logo-marquee-wrap">
        {/* Ignite glow — right edge */}
        <div className="logo-edge logo-edge-right" />
        {/* Short-circuit flicker — left edge */}
        <div className="logo-edge logo-edge-left" />

        <div className="logo-marquee-track">
          {[0, 1].map((copy) => (
            <div key={copy} className="logo-marquee-set" aria-hidden={copy === 1}>
              {logos.map((logo) => (
                <img
                  key={`${copy}-${logo.slug}`}
                  src={`/logos/${logo.slug}.svg`}
                  alt={logo.name}
                  height={LOGO_HEIGHT}
                  className="logo-marquee-img"
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .logo-marquee-wrap {
          position: relative;
          width: 100%;
          overflow: hidden;
        }

        .logo-marquee-track {
          display: flex;
          width: max-content;
          animation: logoScroll ${SCROLL_DURATION}s linear infinite;
        }

        .logo-marquee-set {
          display: flex;
          align-items: center;
          gap: 48px;
          padding-right: 48px;
          flex-shrink: 0;
        }

        .logo-marquee-img {
          height: ${LOGO_HEIGHT}px;
          width: auto;
          display: block;
          opacity: 0.45;
          flex-shrink: 0;
        }

        /* Edge overlays */
        .logo-edge {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 140px;
          z-index: 2;
          pointer-events: none;
        }

        /* Left: short-circuit exit — blue spark into black fade */
        .logo-edge-left {
          left: 0;
          background: linear-gradient(
            to right,
            #0A0A0A 0%,
            #0A0A0A 20%,
            rgba(10, 10, 10, 0.95) 35%,
            rgba(96, 165, 250, 0.06) 60%,
            transparent 100%
          );
          animation: edgeFlickerLeft 3s ease-in-out infinite;
        }

        /* Right: ignite entry — orange glow into black fade */
        .logo-edge-right {
          right: 0;
          background: linear-gradient(
            to left,
            #0A0A0A 0%,
            #0A0A0A 20%,
            rgba(10, 10, 10, 0.95) 35%,
            rgba(255, 99, 0, 0.08) 60%,
            transparent 100%
          );
          animation: edgeFlickerRight 4s ease-in-out infinite;
        }

        @keyframes logoScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        @keyframes edgeFlickerLeft {
          0%, 100% { opacity: 1; }
          40% { opacity: 0.92; }
          42% { opacity: 1; }
          44% { opacity: 0.88; }
          46% { opacity: 1; }
          70% { opacity: 0.95; }
          72% { opacity: 1; }
        }

        @keyframes edgeFlickerRight {
          0%, 100% { opacity: 1; }
          30% { opacity: 0.9; }
          32% { opacity: 1; }
          60% { opacity: 0.85; }
          62% { opacity: 1; }
          64% { opacity: 0.92; }
          66% { opacity: 1; }
        }

        @media (max-width: 660px) {
          .logo-edge { width: 60px; }
          .logo-marquee-set { gap: 32px; padding-right: 32px; }
          .logo-marquee-img { height: 18px; }
        }
      `}</style>
    </Section>
  )
}
